import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useLanguage } from '@/contexts/LanguageContext';
import { useEffect, useState } from 'react';
import {
  Calculator, Users, ShoppingCart, BarChart3, Settings,
  ArrowRight, LogOut, Bell, User, ChevronRight, Lock, Factory,
} from 'lucide-react';
import api from '@/lib/api';

const MODULES = [
  {
    id: 'accounting',
    name: 'Accounting',
    description: 'Financial management, reports, budgets, reconciliation & more',
    icon: Calculator,
    gradient: 'from-blue-600 via-blue-700 to-indigo-800',
    accent: '#3b82f6',
    entryPath: '/accounting',
    features: ['Financial Dashboard', 'P&L & Balance Sheet', 'Journal Entries', 'Budget vs Actual', 'Bank Reconciliation'],
  },
  {
    id: 'hr',
    name: 'HR & Workforce',
    description: 'Employee lifecycle, payroll, attendance, recruitment & compliance',
    icon: Users,
    gradient: 'from-emerald-600 via-emerald-700 to-teal-800',
    accent: '#10b981',
    entryPath: '/hr',
    features: ['Employees & Payroll', 'Attendance & Leave', 'Recruitment', 'Performance Reviews', 'Country Labor Laws'],
  },
  {
    id: 'pos',
    name: 'POS & Sales',
    description: 'Point of sale, inventory, orders, customers & multi-industry workflows',
    icon: ShoppingCart,
    gradient: 'from-amber-500 via-orange-600 to-orange-700',
    accent: '#f59e0b',
    entryPath: '/pos-module',
    features: ['Quick Entry POS', 'Sales & Invoices', 'Inventory & Stock', 'Customers & Loyalty', 'Industry Modules'],
  },
  {
    id: 'reports',
    name: 'Reports & Analytics',
    description: 'BI dashboards, AI insights, forecasting & data visualization',
    icon: BarChart3,
    gradient: 'from-violet-600 via-violet-700 to-purple-800',
    accent: '#8b5cf6',
    entryPath: '/reports',
    features: ['BI Dashboard', 'AI Hub', 'Sales Forecast', 'Cross-Branch Report', 'Data Export'],
  },
  {
    id: 'admin',
    name: 'Administration',
    description: 'System settings, users, roles, branches, compliance & security',
    icon: Settings,
    gradient: 'from-stone-700 via-stone-800 to-stone-900',
    accent: '#78716c',
    entryPath: '/admin',
    features: ['Users & Roles', 'Branches & Assets', 'Audit Trail', 'API Rate Limits', 'Branding & White-Label'],
  },
  {
    id: 'factory',
    name: 'Factory & Manufacturing',
    description: 'Production orders, BOM, raw materials, machines & quality control',
    icon: Factory,
    gradient: 'from-cyan-600 via-blue-600 to-blue-700',
    accent: '#0891b2',
    entryPath: '/factory-dashboard',
    features: ['Production Orders', 'Bill of Materials', 'Raw Materials', 'Machines', 'Quality Control'],
  },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function ModuleCard({ mod, index, allowed }) {
  const navigate = useNavigate();
  const locked = !allowed;

  return (
    <button
      onClick={() => !locked && navigate(mod.entryPath)}
      disabled={locked}
      className={`group relative text-left rounded-2xl overflow-hidden transition-all duration-300 ${
        locked
          ? 'opacity-50 cursor-not-allowed grayscale'
          : 'hover:shadow-xl hover:-translate-y-1 cursor-pointer'
      }`}
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`module-${mod.id}`}
    >
      {/* Gradient Header */}
      <div className={`bg-gradient-to-br ${mod.gradient} px-6 py-6 relative overflow-hidden`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,0.12),transparent_60%)]" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center ring-1 ring-white/20">
              {locked ? <Lock size={22} className="text-white/70" /> : <mod.icon size={22} className="text-white" />}
            </div>
            <div>
              <h3 className="font-outfit font-bold text-white text-lg tracking-tight">{mod.name}</h3>
              <p className="text-white/60 text-xs mt-0.5">{mod.description.slice(0, 50)}...</p>
            </div>
          </div>
          {!locked && (
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
              <ArrowRight size={16} className="text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="bg-white border border-border border-t-0 rounded-b-2xl px-6 py-4">
        <div className="flex flex-wrap gap-1.5">
          {mod.features.slice(0, 4).map((f, i) => (
            <span
              key={i}
              className="text-[10px] px-2.5 py-1 rounded-full bg-muted/50 text-muted-foreground font-medium border border-border"
            >
              {f}
            </span>
          ))}
          {mod.features.length > 4 && (
            <span className="text-[10px] px-2.5 py-1 rounded-full bg-muted/50 text-muted-foreground font-medium border border-border">
              +{mod.features.length - 4} more
            </span>
          )}
        </div>
        {locked && (
          <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
            <Lock size={10} /> Access restricted. Contact admin.
          </p>
        )}
      </div>
    </button>
  );
}

export default function AppLauncher() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const { t } = useLanguage();
  const [stats, setStats] = useState({});

  const tenantModules = JSON.parse(localStorage.getItem('tenant_modules') || '[]');
  const userModules = user?.allowed_modules || [];
  const isAdmin = user?.role === 'admin' || user?.is_super_admin;
  // Admins get all tenant modules, other users get their allowed_modules
  const allowedModules = isAdmin ? [...new Set([...tenantModules, ...userModules, 'factory'])] : (userModules.length > 0 ? userModules : tenantModules);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const greeting = getGreeting();
  const firstName = user?.name ? user.name.split(' ')[0] : '';

  return (
    <div className="min-h-screen bg-muted/50" data-testid="app-launcher">
      {/* Top Bar */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-border/60 px-6 py-3 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')} data-testid="app-launcher-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-stone-800 to-stone-950 flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm tracking-tighter">S</span>
            </div>
            <div>
              <h1 className="font-outfit font-bold text-foreground text-base leading-tight tracking-tight">Smarto</h1>
              <p className="text-[10px] text-muted-foreground font-medium">{user?.company_name || 'Enterprise Suite'}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/notifications')} className="p-2.5 rounded-xl hover:bg-muted transition-colors" data-testid="notifications-btn">
              <Bell size={17} className="text-muted-foreground" />
            </button>
            <button onClick={() => navigate('/my-portal')} className="p-2.5 rounded-xl hover:bg-muted transition-colors" data-testid="portal-btn">
              <User size={17} className="text-muted-foreground" />
            </button>
            <button onClick={logout} className="p-2.5 rounded-xl hover:bg-error/10 transition-colors" data-testid="logout-btn">
              <LogOut size={17} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <p className="text-sm text-muted-foreground font-medium mb-1">{greeting}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold text-foreground tracking-tight" data-testid="welcome-heading">
            {firstName || 'Welcome'}
          </h2>
          <p className="text-muted-foreground mt-2 text-sm max-w-md">Select a module to begin. Each workspace is tailored to your role.</p>
        </div>

        {/* Module Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {MODULES.map((mod, i) => (
            <ModuleCard
              key={mod.id}
              mod={mod}
              index={i}
              allowed={allowedModules.includes(mod.id)}
            />
          ))}
        </div>

        {/* Quick Access */}
        <div className="mt-14 pt-8 border-t border-border/60">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-4">Quick Access</p>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Daily Summary', path: '/daily-summary' },
              { label: 'Quick POS Entry', path: '/pos' },
              { label: 'My Portal', path: '/my-portal' },
              { label: 'Notifications', path: '/notifications' },
              { label: 'Dashboard', path: '/dashboard' },
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-border text-xs font-medium text-muted-foreground hover:border-border hover:shadow-sm transition-all duration-200"
                data-testid={`quick-${item.path.replace('/', '')}`}
              >
                {item.label}
                <ChevronRight size={12} className="text-muted-foreground group-hover:text-muted-foreground group-hover:translate-x-0.5 transition-all" />
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
